import { CategoryOption } from 'src/assets/static/view_department/useDepartmentCategories';
import { GLBreakoutLineItems } from 'src/services/useInvoiceGlBreakoutApi';
import { round } from '.';
export const fileBaseUrl = process.env.REACT_APP_FILES_URL;
export const SUPER_ADMIN_ID = '4514165225';
export const SUPER_ADMIN_COMPANY_NAME = 'AVENDRA SUPER ADMIN';
export const MIN_LINE_ITEMS = 2;

export interface CommonUrls {
  viewInvoices: string;
  viewReceipts: (companyId?: string) => string;
  invoiceDetails: (receiptId: string, poId: string) => string;
  receiptDetails: (receiptId: string, poId: string, isEnterReceipts?: boolean) => string;
  poDetails: (poId: string) => string;
  enterReceipt: (poNumber: string, companyId: string) => string;
  enterInvoice: (
    companyId: string,
    docNumber?: string,
    poNumber?: string,
    isOcrRedirect?: boolean,
    attachId?: string
  ) => string;
  varianceDetails: (poId: string, varianceId: string) => string;
  variances: string;
  createPo: string;
  manageSupplier: string;
  accrual: string;
  checkbook: string;
}

interface RedirectUrls {
  reactUrls: CommonUrls;
  devUrls: CommonUrls;
}

const appUrl = process.env.REACT_APP_LEGACY_URL;

export const redirectUrls: RedirectUrls = {
  reactUrls: {
    viewInvoices: '/view-invoices',
    viewReceipts: (companyId) => `/view-receipts${companyId ? '?companyId=' + companyId : ''}`,
    invoiceDetails: (receiptId, poId) => `/invoicedetails?recid=${receiptId}&poid=${poId}`,
    receiptDetails: (receiptId, poId, isEnterReceipts) =>
      `/po/receiptdetails?recid=${receiptId}&poid=${poId}${
        isEnterReceipts ? '&from=enter_receipts' : ''
      }`,
    poDetails: (poId) => `${appUrl}/Buyer/PurchaseOrder/Detail?id=${poId}`,
    enterReceipt: (poNumber, companyId) =>
      `/create-receipt?poNumber=${poNumber}&companyId=${companyId}`,
    enterInvoice: (companyId?, docNumber?, poNumber?, isOcrRedirect?, attachId?) =>
      `/enter-invoice?companyId=${companyId}${docNumber ? '&docNumber=' + docNumber : ''}${
        poNumber ? '&poNumber=' + poNumber : ''
      }${isOcrRedirect ? '&isOcrRedirect=' + isOcrRedirect : ''}${
        attachId ? '&attachId=' + attachId : ''
      }`,
    varianceDetails: (poId, varianceId) =>
      `/variance-details?poId=${poId}&varianceId=${varianceId}`,
    createPo: `${appUrl}/Buyer/OfflinePurchaseOrder/CreateOfflinePO`,
    manageSupplier: `${appUrl}/Buyer/OfflinePurchaseOrder/ManageSupplier`,
    variances: `/variances`,
    accrual: `${appUrl}/Buyer/Reports/Accrual`,
    checkbook: '/view-checkbook',
  },

  devUrls: {
    viewInvoices: '/Buyer/Invoices/ViewInvoices',
    viewReceipts: (companyId?: string) =>
      `/buyer/Receiving/ViewReceipts${companyId ? '?companyId=' + companyId : ''}`,
    invoiceDetails: (receiptId, poId) =>
      `/buyer/invoices/invoicedetails?poid=${poId}&recid=${receiptId}`,
    receiptDetails: (receiptId, poId, isEnterReceipts) =>
      `/buyer/receiving/receiptdetails?recid=${receiptId}&poid=${poId}${
        isEnterReceipts ? '&from=enter_receipts' : ''
      }`,
    poDetails: (poId) => `/Buyer/PurchaseOrder/Detail?id=${poId}`,
    enterReceipt: (poNumber, companyId) =>
      `/Buyer/Receiving/EnterReceipt?poNumber=${poNumber}&companyId=${companyId}`,
    enterInvoice: (companyId?, docNumber?, poNumber?, isOcrRedirect?, attachId?) =>
      `/Buyer/Invoices/EnterInvoice?companyId=${companyId}${
        docNumber ? '&docNumber=' + docNumber : ''
      }${poNumber ? '&poNumber=' + poNumber : ''}${
        isOcrRedirect ? '&isOcrRedirect=' + isOcrRedirect : ''
      }${attachId ? '&attachId=' + attachId : ''}`,
    varianceDetails: (poId, varianceId) =>
      `/buyer/receiving/variancedetails?poId=${poId}&varianceId=${varianceId}`,
    createPo: '/Buyer/OfflinePurchaseOrder/CreateOfflinePO',
    manageSupplier: '/Buyer/OfflinePurchaseOrder/ManageSupplier',
    variances: `/Buyer/Receiving/Variances`,
    accrual: `/Buyer/Reports/Accrual`,
    checkbook: '/Buyer/Checkbook',
  },
} as const;

export const languageCodes = {
  '1033': 'en',
  '4105': 'ca',
  '17418': 'es',
  '1036': 'fr',
};
export const LanguageCategories: CategoryOption[] = [
  { value: 1033, label: 'en' },
  { value: 4105, label: 'ca' },
  { value: 17418, label: 'es' },
  { value: 1036, label: 'fr' },
];
export const invoiceDirtyMessages = ['Please save the document.'];
export const receiptDirtyMessages = ['Please save the receipt.'];

export const percentageCal = (percentageAmount: number, amt: number) =>
  round(Number(percentageAmount || 0) * 0.01 * Number(amt || 0), 2);

export const getHigherValueIndex = (items: any[], value: number, key: string) => {
  const sum = items?.reduce((ac: any, co: any) => ac + +co?.[key].toFixed(2), 0);
  const difference = +(value - sum).toFixed(2);
  if (difference !== 0 && !Number.isNaN(difference)) {
    const valuesArray = items.map((item: any) => item[key]);
    const higherValueIndex = valuesArray.findIndex(
      (val: number) => val === Math.max(...valuesArray)
    );
    return { difference, higherValueIndex };
  }
};

export const fixRoundup = (
  items: GLBreakoutLineItems[],
  subTotalAmt: number,
  taxableAmt: number,
  discount: number,
  shipping: number,
  taxes: number,
  total: number
) => {
  const totalProps = getHigherValueIndex(items, total, 'glTotal');
  const subTotalProps = getHigherValueIndex(items, subTotalAmt, 'glSubTotal');
  const taxableAmountProps = getHigherValueIndex(items, taxableAmt, 'taxableAmount');
  const discountProps = getHigherValueIndex(items, discount, 'discountAmt');
  const shippingProps = getHigherValueIndex(items, shipping, 'shipCostAmt');
  const taxesProps = getHigherValueIndex(items, taxes, 'salesTaxAmt');

  if (totalProps?.difference && items[totalProps?.higherValueIndex]) {
    items[totalProps?.higherValueIndex].glTotal += totalProps?.difference;
  }
  if (subTotalProps?.difference && items[subTotalProps?.higherValueIndex]) {
    items[subTotalProps?.higherValueIndex].glSubTotal += subTotalProps?.difference;
  }
  if (taxableAmountProps?.difference && items[taxableAmountProps?.higherValueIndex]) {
    items[taxableAmountProps?.higherValueIndex].taxableAmount += taxableAmountProps?.difference;
  }
  if (discountProps?.difference && items[discountProps?.higherValueIndex]) {
    items[discountProps?.higherValueIndex].discountAmt += discountProps?.difference;
  }
  if (shippingProps?.difference && items[shippingProps?.higherValueIndex]) {
    items[shippingProps?.higherValueIndex].shipCostAmt += shippingProps?.difference;
  }
  if (taxesProps?.difference && items[taxesProps?.higherValueIndex]) {
    items[taxesProps?.higherValueIndex].salesTaxAmt += taxesProps?.difference;
  }
  return items;
};

export const extractNumericPart = (str: string) => {
  const numericPart = str.match(/\d+/);

  if (numericPart) {
    return numericPart[0];
  } else {
    return null;
  }
};

export const generateRegexPattern = (patterns: string[], testString?: string): boolean => {
  // Define the mappings
  const mappings: { [key: string]: string } = {
    '*': '[a-zA-Z0-9]',
    '#': '\\d',
    '@': '[a-zA-Z]',
    '-': '\\-',
    '.': '\\.',
    _: '_',
    '(': '\\(',
    ')': '\\)',
  };
  const regexes = patterns.map((pattern) => {
    let regexPattern = '';
    for (let char of pattern) {
      if (mappings[char]) {
        regexPattern += mappings[char];
      } else {
        regexPattern += char;
      }
    }
    return new RegExp(`^${regexPattern}$`);
  });

  if (testString !== undefined) {
    return regexes.some((regex) => regex.test(testString));
  }

  return true;
};

export const formatPeriod = (period: string) => {
  const periodNumber = parseInt(period, 10);

  return periodNumber;
};
